import parseHtml from 'html-react-parser'
import { DateTime } from 'luxon'
import { RefCallback, useCallback, useEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useNavigate } from 'react-router-dom'

import translations from '@translationKeys'
import { Nullable } from '@types'

import SelectWithLabeledValue from '@components/SelectWithLabeledValue'
import TruncateLines from '@components/TruncateLines'
import { CssTruncation } from '@components/TruncateLines/index.styles'
import SendMessageButtonIcon from '@components/icons/SendMessageButtonIcon'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import {
  MessageAuthorWrapper,
  MessageAvatar,
  MessageBox,
  MessageBoxText,
  MessageHeader,
  MessageHeaderAuthor,
  MessageSenderInitials,
} from '@modules/contacts/components/ContactsHistoryPopover/MessageStreamList.styles'
import { CancelSearchIconWrapper } from '@modules/search/components/SearchSection/index.styles'
import { ProfileType } from '@modules/search/types'
import CancelIcon from '@mui/icons-material/Cancel'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQueryParams } from '@shared/hooks/useQueryParams'
import { useWindowResize } from '@shared/hooks/useWindowResize'
import {
  clearChatIdToOpenFromLocalStorage,
  clearChatModalOpenFromLocalStorage,
  delay,
  isFutureDate,
  isSameDay,
  removeHtmlTags,
  retrieveChatIdToOpenFromLocalStorage,
  retrieveChatModalOpenFromLocalStorage,
} from '@shared/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import chatApi from './api'
import ChatAvatar from './components/ChatAvatar'
import ChatDateTime from './components/ChatDateTime'
import ChatItemPreview from './components/ChatItemPreview'
import ChatPanel from './components/ChatPanel'
import ChatsSkeleton from './components/ChatsSkeleton'
import DateDivider from './components/DateDivider'
import NoMessageItem from './components/NoMessageItem'
import {
  ChatMessageInputWrapper,
  ChatModalInner,
  ChatModalWrapper,
  ClearInputContainer,
  LoadMoreMessagesAnchor,
  MessageListContainer,
  MessageWrapper,
  SortByWrapper,
  SubmitMessageButtonWrapper,
} from './components/index.styles'
import { Chat, ChatMember, ChatMessage, LocalChat, SortChatType } from './types'
import { sanitizeMessageHtml } from './utils'

interface ChatModalProps {
  isOpen: boolean
  closeChatModal: () => void
  openChatModal: () => void
  chats: Chat[]
  isLoading: boolean
  setOpenedChat: (ch: Nullable<Chat>) => void
  setLocalOpenedChat?: (ch: Nullable<LocalChat>) => void
  openedChat?: Nullable<Chat>
  openedLocalChat?: Nullable<LocalChat>
  messages?: ChatMessage[]
  members?: ChatMember[]
  loadMoreMessagesRef?: RefCallback<HTMLElement>
  loadMoreChatsRef?: RefCallback<HTMLElement>
  selectedChatsSort: SortChatType
  handleSortChange: (value: SortChatType) => void
  unreadCounts: { total: number; byChatId: Record<Chat['id'], number>; markedChats?: number[] }
  openChatModalByChatId: (chatId: number) => void
}

const ChatModal = (props: ChatModalProps) => {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const {
    isLoading,
    isOpen,
    chats,
    openedChat,
    openedLocalChat,
    messages,
    members,
    loadMoreMessagesRef,
    loadMoreChatsRef,
    closeChatModal,
    openChatModal,
    setOpenedChat,
    setLocalOpenedChat,
    selectedChatsSort,
    handleSortChange,
    unreadCounts,
    openChatModalByChatId,
  } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [newMessage, setNewMessage] = useState('')
  const messagesScrollContainer = useRef<HTMLElement>(null)
  const [xPosition, setXPosition] = useState<Nullable<number>>(null)
  const chatWrapperRef = useRef<HTMLDivElement>(null)
  const { windowWidth = 0 } = useWindowResize()
  const [scrollPosition, setScrollPosition] = useState(0)
  const storagePositionKey = `chatX:${user?.id || ''}`
  const reactQuillRef = useRef<ReactQuill | null>(null)

  const { get, clearOneSilently } = useQueryParams()

  const openQsKey = 'chat-modal-open'
  const openQsValue = get(openQsKey)

  const chatIdToOpenQsKey = 'chat-id-to-open'
  const chatIdToOpenValue = get(chatIdToOpenQsKey)

  useEffect(() => {
    if (!openedChat && messagesScrollContainer.current) {
      messagesScrollContainer.current.scrollTop = scrollPosition
    }
  }, [openedChat, scrollPosition])

  useEffect(() => {
    if (openQsValue) {
      openChatModal()
      clearOneSilently(openQsKey)
    }
  }, [openQsValue])

  useEffect(() => {
    const { chatModalOpen } = retrieveChatModalOpenFromLocalStorage()

    if (chatModalOpen) {
      openChatModal()

      clearChatModalOpenFromLocalStorage()
    }
  }, [])

  useEffect(() => {
    if (chatIdToOpenValue) {
      openChatModalByChatId(Number(chatIdToOpenValue))
      clearOneSilently(chatIdToOpenQsKey)
    }
  }, [chatIdToOpenValue])

  useEffect(() => {
    const { chatIdToOpen } = retrieveChatIdToOpenFromLocalStorage()

    if (chatIdToOpen) {
      openChatModalByChatId(chatIdToOpen)

      clearChatIdToOpenFromLocalStorage()
    }
  }, [])

  useEffect(() => {
    let savedXPosition = Number(localStorage.getItem(storagePositionKey))

    const chatWidth = chatWrapperRef?.current?.getBoundingClientRect?.().width ?? 0
    // Ensure savedXPosition doesn't go beyond screen width.
    if (Math.abs(savedXPosition) + chatWidth > windowWidth) {
      savedXPosition = -(windowWidth - chatWidth - 40)
    }

    setXPosition(savedXPosition ?? 0)
  }, [windowWidth, chatWrapperRef, storagePositionKey])

  const handleDragStop = (_: unknown, data: { x: number }) => {
    setXPosition(data.x)

    localStorage.setItem(storagePositionKey, data.x.toString())
  }

  const toggleChatModal = useCallback(() => (isOpen ? closeChatModal() : openChatModal()), [isOpen])

  useEffect(() => {
    if (messagesScrollContainer?.current) {
      messagesScrollContainer.current?.scrollTo?.({ top: messagesScrollContainer.current.scrollHeight })
    }
  }, [messagesScrollContainer])

  const { mutate: submitNewMessage } = useMutation({
    mutationFn: chatApi.sendMessage.fn,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [chatApi.getChats.key] })
      queryClient.invalidateQueries({ queryKey: [chatApi.getChatMessages.key, openedChat?.id] })
      await delay(500)
      messagesScrollContainer?.current?.scrollTo({ top: messagesScrollContainer.current.scrollHeight })
    },
  })

  const { mutate: createChat } = useMutation({
    mutationFn: chatApi.createChat.fn,
    onSuccess: chat => {
      setOpenedChat({ ...openedLocalChat, ...chat })
      setLocalOpenedChat?.(null)
      queryClient.refetchQueries({ queryKey: [chatApi.getChatMessages.key, chat?.id] })
      queryClient.invalidateQueries({ queryKey: [chatApi.getChats.key] })
    },
  })

  const handleNewMessageSubmit = (latestMessage?: string) => {
    if (!newMessage && !latestMessage) {
      return
    }

    const messageToProcess = latestMessage || newMessage

    const sanitizedMessage = sanitizeMessageHtml(messageToProcess)

    if (openedLocalChat) {
      createChat({
        toDelegateIds: openedLocalChat.withDelegates as number[],
        message: sanitizedMessage,
        toCompanyId: openedLocalChat.withCompanyId,
        chatType: openedLocalChat.type,
      })
      setNewMessage('')

      return
    }

    if (!openedChat) {
      return
    }

    submitNewMessage({ chatId: openedChat.id, content: sanitizedMessage })
    setNewMessage('')
  }
  const handleClick = (chat: Chat) => {
    const currentScrollPosition = messagesScrollContainer.current?.scrollTop || 0
    setScrollPosition(currentScrollPosition)
    setOpenedChat(chat)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // this is needed to prevent extra line being added by Quill before message is submitted
      const editor = reactQuillRef.current?.getEditor() as unknown as { history: { undo: () => void } }
      editor?.history.undo()
      handleNewMessageSubmit(reactQuillRef.current?.value.toString())
    }
  }

  const goToDelegateProfile = (delegateId: number) => {
    if (delegateId && !membersMap?.[delegateId]?.isHidden) {
      navigate(`/plus/profile/${delegateId}/${ProfileType.delegate}`)
    }
  }

  const isEmptyMessage = !removeHtmlTags(newMessage)?.trim()
  const { hasAccessToChat, cantInitiateChats } = useFeatureFlags()
  const membersMap = members?.reduce((acc, i) => ({ ...acc, [i.id]: i }), {} as Record<number, ChatMember>)
  const chatToDisplay = openedLocalChat || openedChat

  return xPosition !== null ? (
    <Draggable axis="x" handle=".handle" bounds="#root" position={{ x: xPosition, y: 0 }} onStop={handleDragStop}>
      <ChatModalWrapper isExpanded={isOpen} isChatOpened={isOpen && !!chatToDisplay} ref={chatWrapperRef}>
        <ChatPanel
          openedChat={chatToDisplay as Nullable<LocalChat>}
          unreadCount={unreadCounts?.total}
          isExpanded={isOpen}
          toggleChatModal={toggleChatModal}
          goBack={() => {
            setOpenedChat(null)
            setLocalOpenedChat?.(null)
          }}
        />

        {isOpen && (
          <ChatModalInner ref={messagesScrollContainer} $reverse={openedChat && !openedLocalChat}>
            {isLoading && <ChatsSkeleton />}
            {((!!openedLocalChat && !openedChat && !hasAccessToChat) ||
              (!openedChat && openedLocalChat && cantInitiateChats)) && <NoMessageItem />}
            {!chatToDisplay && isOpen && (
              <div>
                {!chats?.length ? (
                  <NoMessageItem />
                ) : (
                  <>
                    <SortByWrapper>
                      <Box mb="16px">
                        <SelectWithLabeledValue
                          selectedOptionId={selectedChatsSort as unknown as number}
                          onSelect={value => handleSortChange(value as unknown as SortChatType)}
                          label={t(translations.SORT_BY)}
                          options={[
                            { label: t(translations.MOST_RECENT), id: SortChatType.updated as unknown as number },
                            { label: t(translations.MOST_UNREAD), id: SortChatType.unread as unknown as number },
                          ]}
                        />
                      </Box>
                    </SortByWrapper>
                    {chats.map(chat => (
                      <Box mb="16px" key={chat.id} marginRight="10px">
                        <ChatItemPreview
                          chat={chat}
                          onClick={() => handleClick(chat)}
                          unreadCount={unreadCounts?.byChatId?.[chat.id]}
                          showEmptyUnreadCount={unreadCounts?.markedChats?.includes(chat.id)}
                        />
                      </Box>
                    ))}
                    <Box ref={loadMoreChatsRef} />
                  </>
                )}
              </div>
            )}

            {isOpen && openedChat && !openedLocalChat && (
              <MessageListContainer>
                <LoadMoreMessagesAnchor ref={loadMoreMessagesRef} />
                {messages?.map((msg, idx) => (
                  <MessageWrapper key={msg.id}>
                    {!isSameDay(DateTime.fromISO(msg.createdAt), DateTime.fromISO(messages[idx - 1]?.createdAt)) && (
                      <DateDivider date={msg.createdAt} />
                    )}
                    <MessageHeader $isDisabled={membersMap?.[msg.p1UserId]?.isHidden}>
                      {membersMap?.[msg.p1UserId]?.profilePicture ? (
                        <MessageAvatar avatarSrc={membersMap?.[msg.p1UserId]?.profilePicture} />
                      ) : (
                        <MessageSenderInitials>{`${membersMap?.[msg.p1UserId]?.firstName[0]} ${
                          membersMap?.[msg.p1UserId]?.lastName[0]
                        } `}</MessageSenderInitials>
                      )}
                      <MessageAuthorWrapper>
                        <MessageHeaderAuthor
                          onClick={() => goToDelegateProfile(msg.p1UserId)}
                          $isDisabled={membersMap?.[msg.p1UserId]?.isHidden}
                        >
                          {`${membersMap?.[msg.p1UserId]?.firstName} ${membersMap?.[msg.p1UserId]?.lastName}`}:
                        </MessageHeaderAuthor>
                        <ChatDateTime format="T" date={msg.createdAt} sx={{ fontSize: '13px' }} />
                      </MessageAuthorWrapper>
                    </MessageHeader>
                    <MessageBox msg={{ isMine: msg.p1UserId === user?.id }} $isDisabled={membersMap?.[msg.p1UserId]?.isHidden}>
                      <MessageBoxText>{parseHtml(msg.content)}</MessageBoxText>
                    </MessageBox>
                  </MessageWrapper>
                ))}
              </MessageListContainer>
            )}

            {isOpen && openedLocalChat && hasAccessToChat && (openedChat || !cantInitiateChats) && (
              <Box display="flex" alignItems="center" flexDirection="column" mt="16px">
                <Box mb="16px" mr="-10px">
                  <ChatAvatar chat={openedLocalChat} width="47px" height="47px" />
                </Box>
                <Typography variant="caption" textAlign="center">
                  <TruncateLines lines={1}>{openedLocalChat.name}</TruncateLines>
                </Typography>
                {openedLocalChat.subTitle && (
                  <Typography variant="body1" textAlign="center">
                    <TruncateLines lines={2}>{openedLocalChat.subTitle}</TruncateLines>
                  </Typography>
                )}
                {isFutureDate(openedLocalChat?.eventData?.startDate || '') && (
                  <>
                    <Typography variant="body1" sx={{ marginTop: '16px' }}>
                      {t(translations.ATTENDING)}
                    </Typography>
                    <Typography variant="body1" textAlign="center">
                      <TruncateLines lines={1}>{openedLocalChat?.eventData?.name}</TruncateLines>
                    </Typography>
                    {!user?.attendEvents[openedLocalChat?.eventData?.id as number] && openedLocalChat?.eventData?.website && (
                      <Button
                        sx={{ marginTop: '16px' }}
                        size="large"
                        color="primary"
                        variant="contained"
                        href={openedLocalChat?.eventData?.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t(translations.SIGN_UP_TO)}{' '}
                        <Box ml="5px" fontWeight="700">
                          <CssTruncation maxWidth="200px">{openedLocalChat?.eventData?.name}</CssTruncation>
                        </Box>
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}
          </ChatModalInner>
        )}

        {isOpen && (!!openedChat || (!!openedLocalChat && hasAccessToChat)) && (openedChat || !cantInitiateChats) && (
          <ChatMessageInputWrapper>
            <ReactQuill
              modules={{
                toolbar: [['bold', 'italic', { list: 'bullet' }, { list: 'ordered' }]],
                history: {
                  delay: 10,
                },
              }}
              ref={el => {
                reactQuillRef.current = el
              }}
              value={newMessage}
              onChange={setNewMessage}
              onKeyDown={handleKeyPress}
            />
            {!isEmptyMessage && (
              <ClearInputContainer>
                <CancelSearchIconWrapper onClick={() => setNewMessage('')} data-testid="clear-chat-message-icon">
                  <CancelIcon htmlColor={colors.lightGrey} />
                </CancelSearchIconWrapper>
              </ClearInputContainer>
            )}
            <SubmitMessageButtonWrapper>
              <IconButton onClick={() => handleNewMessageSubmit()} disabled={isEmptyMessage} color="primary">
                <SendMessageButtonIcon fontSize="small" />
              </IconButton>
            </SubmitMessageButtonWrapper>
          </ChatMessageInputWrapper>
        )}
      </ChatModalWrapper>
    </Draggable>
  ) : null
}

export default ChatModal
